import { Show, useObservable } from "@legendapp/state/react"
import { Pressable, View, Modal, ActivityIndicator, Platform } from "react-native"
import { useState } from "react"
import { useDebounce } from "use-debounce"
import { SafeAreaView } from "react-native-safe-area-context"

import { trpc } from "utils/trpc"
import { ar } from "@src/i18n"
import { cn, sharedStyles } from "@src/utils"

import { Text, TextInput, MCIcon } from "./core"
import { List } from "./list"
import { Header } from "./Header"

type Props = {
  value: number[]
  onSelectionChange: (ids: number[]) => void
  className?: string
}

export function SupplierPicker({ value, onSelectionChange, className }: Props) {
  const visible$ = useObservable(false)
  const supplier = trpc.suppliers.get.useQuery({ id: value[0] as number }, { enabled: value.length > 0 })

  return (
    <>
      <Pressable className={cn("min-h-[45px] bg-gray-50 border border-gray-300 rounded px-3 justify-center active:opacity-70", className)} onPress={() => visible$.set(true)}>
        <Text className={`text-left ${supplier.data ? "text-gray-900" : "text-gray-500"}`}>{supplier.data ? supplier.data.name : ar.selectSupplier}</Text>
      </Pressable>

      <Show if={visible$}>
        <SupplierPickModal value={value} onSelectionChange={onSelectionChange} hide={() => visible$.set(false)} />
      </Show>
    </>
  )
}

function SupplierPickModal({ value, onSelectionChange, hide }: Props & { hide: () => void }) {
  const [searchText, setSearchText] = useState("")
  const [debouncedSearchText] = useDebounce(searchText, 300)

  const _suppliers = trpc.suppliers.list.useInfiniteQuery({ search: debouncedSearchText.trim() || undefined }, { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined })

  const suppliers = _suppliers.data?.pages.flatMap((page) => page.items) ?? []
  const ListEmptyComponent = _suppliers.isLoading ? (
    <View className="flex-1 justify-center items-center py-12">
      <ActivityIndicator size="large" color="#3B82F6" />
      <Text className="mt-4 text-gray-600">{ar.loading}</Text>
    </View>
  ) : (
    <Text className="text-gray-500 text-center py-8">{ar.noSuppliersFound}</Text>
  )

  return (
    <Modal animationType="slide" presentationStyle="formSheet" allowSwipeDismissal onDismiss={hide} onRequestClose={hide}>
      <Header
        title={ar.suppliers}
        withoutInsets={Platform.OS === "ios"}
        right={
          <Pressable onPress={hide} className="px-2 active:opacity-70 h-full justify-center items-center">
            <MCIcon name="close" size={25} />
          </Pressable>
        }
      />

      <View className="w-full max-w-content mx-auto px-2 mb-2">
        <TextInput placeholder={ar.searchSuppliers} onChangeText={setSearchText} autoComplete="off" autoCapitalize="none" value={searchText} />
      </View>

      <List
        data={suppliers}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={sharedStyles.contentContainer}
        onEndReached={() => {
          if (_suppliers.hasNextPage && !_suppliers.isFetchingNextPage) _suppliers.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={ListEmptyComponent}
        renderItem={({ item: supplier }) => {
          const isSelected = value.includes(supplier.id)

          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-300 active:opacity-70"
              onPress={() => {
                onSelectionChange([supplier.id])
                hide()
              }}
            >
              <Text className={isSelected ? "text-blue-600 font-semibold" : "text-gray-900"}>{supplier.name}</Text>
              {supplier.phones.length > 0 && <Text className="text-sm text-gray-500 mt-0.5">{supplier.phones.join(" · ")}</Text>}
            </Pressable>
          )
        }}
        ListFooterComponent={
          <>
            {_suppliers.isFetchingNextPage && <ActivityIndicator className="py-4" />}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />
    </Modal>
  )
}
